import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, Plus, MoreHorizontal, CalendarDays, X, LayoutGrid, List, Handshake, CircleDollarSign, WalletCards, Activity, ChevronRight } from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'
import {
  getCompletedDeliveryCount,
  getDeliveryProgress,
  getProgressLabel,
  getTimelineStatus
} from '../lib/healthStatus'
import ConfirmModal from '../ConfirmModal'
import { sumInCurrency } from '../lib/currencyConversion'

const REPORTING_CURRENCIES = ['GBP', 'EUR', 'USD', 'AUD', 'CAD']

const EMPTY_FORM = {
  client_id: '',
  name: '',
  deal_type: '',
  value: '',
  currency: 'GBP',
  start_date: '',
  end_date: '',
  status: 'active',
  notes: '',
  assigned_to: '',
  payment_terms: '',
  business_unit_ids: []
}

const EMPTY_ALLOC = { project_id: '', amount: '', notes: '', rights: [] }

const RIGHT_OPTIONS = [
  'Social Media',
  'Branding',
  'Hospitality',
  'Broadcast',
  'Digital',
  'Content',
  'Event Activation',
  'VIP Access',
  'Merchandise',
  'Email / Newsletter',
  'Speaking Slot',
  'Custom'
]

const EMPTY_RIGHT_MODAL_FORM = {
  category: 'Social Media',
  custom_name: '',
  quantity: '1',
  notes: ''
}

function getRightName(form) {
  if (form.category === 'Custom') return form.custom_name.trim()
  return form.category
}

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatMoney(value, currency = 'GBP') {
  const amount = Number(value || 0)
  if (amount >= 1000000) return `${currencySymbol(currency)}${(amount / 1000000).toFixed(2)}M`
  if (amount >= 1000) return `${currencySymbol(currency)}${Math.round(amount / 1000)}k`
  return `${currencySymbol(currency)}${amount.toLocaleString()}`
}

function formatCurrencyTotals(totals) {
  const entries = Object.entries(totals)
    .filter(([, value]) => value > 0)
    .sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB))

  if (entries.length === 0) return '—'

  return entries
    .map(([currency, value]) => formatMoney(value, currency))
    .join(' · ')
}

function formatWithCommas(val) {
  if (!val && val !== 0) return ''
  const num = val.toString().replace(/,/g, '')
  if (isNaN(num) || num === '') return val
  const parts = num.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

function stripCommas(val) {
  return val.toString().replace(/,/g, '')
}

function parseMoneyInput(value) {
  const parsed = Number(stripCommas(String(value || '').trim()))
  return Number.isFinite(parsed) ? parsed : 0
}

export default function Deals() {
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { orgId, can, activeBusinessUnitId, businessUnits } = useUser()
  const canViewFinancials = can('financials.view')
  const canManageDeals = can('deals.manage')
  const canManageFinancials = can('financials.manage')

  const [deals, setDeals] = useState([])
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [profiles, setProfiles] = useState([])
  const [allocationsMap, setAllocationsMap] = useState({})
  const [rightsMap, setRightsMap] = useState({})
  const [deliveriesMap, setDeliveriesMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterClient, setFilterClient] = useState('')
  const [sortBy, setSortBy] = useState('value_desc')
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('delivr-deals-view') || '')
  const [reportingCurrency, setReportingCurrency] = useState('GBP')
  const [rateSnapshot, setRateSnapshot] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [allocations, setAllocations] = useState([{ ...EMPTY_ALLOC }])
  const [editingDealId, setEditingDealId] = useState(null)
  const [openActionMenu, setOpenActionMenu] = useState(null)

  const [showRightModal, setShowRightModal] = useState(false)
  const [rightModalAllocationIndex, setRightModalAllocationIndex] = useState(null)
  const [rightModalForm, setRightModalForm] = useState(EMPTY_RIGHT_MODAL_FORM)

  const actionMenuRef = useRef(null)

useEffect(() => {
  fetchAll()
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeBusinessUnitId, orgId])

useEffect(() => {
  if (!orgId) return
  const storedCurrency = localStorage.getItem(`delivr.reporting-currency.${orgId}`)
  setReportingCurrency(REPORTING_CURRENCIES.includes(storedCurrency) ? storedCurrency : 'GBP')
  fetchExchangeRates()
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [orgId])

useEffect(() => {
  const params = new URLSearchParams(location.search)
  const initialSearch = params.get('search')

  if (initialSearch) {
    setSearch(initialSearch)
  }
}, [location.search])

useEffect(() => {
  const editDealId = location.state?.editDealId
  if (!editDealId || loading) return

  const dealToEdit = deals.find(deal => deal.id === editDealId)
  if (!dealToEdit) return

  openEditDeal(dealToEdit)
  navigate(location.pathname, { replace: true, state: null })
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.state, loading, deals])

useEffect(() => {
  function handleClickOutside(event) {
    if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
      setOpenActionMenu(null)
    }
  }

  if (openActionMenu) {
    document.addEventListener('mousedown', handleClickOutside)
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside)
  }
}, [openActionMenu])

  async function fetchAll() {
    setError(null)
    setLoading(true)

    const [
      { data: dealsData, error: dealsError },
      { data: clientsData },
      { data: projectsData },
      { data: profilesData },
      { data: allocData },
      { data: rightsData },
      { data: deliveryData },
      { data: dealBusinessData, error: dealBusinessError },
      { data: clientBusinessData },
    ] = await Promise.all([
      supabase.from('secure_deals').select('*').eq('org_id', orgId).order('created_at', { ascending: false }),
      supabase.from('clients').select('id, name').eq('org_id', orgId).order('name'),
      supabase.from('secure_projects').select('id, name, business_unit_id').eq('org_id', orgId).order('name'),
      supabase.from('profiles').select('id, email, full_name').eq('org_id', orgId).order('full_name'),
      supabase.from('secure_deal_project_allocations').select('*').eq('org_id', orgId),
      supabase.from('rights').select('id, deal_id, project_id, name, right_type, quantity, notes').eq('org_id', orgId),
      supabase.from('secure_deliveries').select('id, status, delivery_date, custom_fields, right_id').eq('org_id', orgId),
      supabase.from('deal_business_units').select('deal_id, business_unit_id').eq('org_id', orgId),
      supabase.from('client_business_units').select('client_id, business_unit_id').eq('org_id', orgId),
    ])

    if (dealsError) {
      console.error('Failed to load deals:', dealsError)
      setError(`Failed to load deals: ${dealsError.message}`)
      setLoading(false)
      return
    }
    if (dealBusinessError) {
      console.error('Failed to load Deal Business assignments:', dealBusinessError)
      setError(`Failed to load Deal Business assignments: ${dealBusinessError.message}`)
      setLoading(false)
      return
    }

    const aMap = {}
    ;(allocData || []).forEach(a => {
      if (!aMap[a.deal_id]) aMap[a.deal_id] = []
      aMap[a.deal_id].push(a)
    })

    const rMap = {}
    ;(rightsData || []).forEach(r => {
      if (!rMap[r.deal_id]) rMap[r.deal_id] = []
      rMap[r.deal_id].push(r)
    })

    const dMap = {}
    ;(deliveryData || []).forEach(delivery => {
      const dealId = delivery.rights?.deal_id
      if (!dealId) return

      if (!dMap[dealId]) dMap[dealId] = []
      dMap[dealId].push(delivery)
    })

    const businessIdsByDeal = (dealBusinessData || []).reduce((map, link) => {
      if (!map[link.deal_id]) map[link.deal_id] = []
      map[link.deal_id].push(link.business_unit_id)
      return map
    }, {})
    const visibleDealIds = new Set((dealBusinessData || []).filter(link => !activeBusinessUnitId || link.business_unit_id === activeBusinessUnitId).map(link => link.deal_id))
    const visibleClientIds = new Set((clientBusinessData || []).filter(link => !activeBusinessUnitId || link.business_unit_id === activeBusinessUnitId).map(link => link.client_id))
    setDeals((dealsData || []).map(deal => ({ ...deal, business_unit_ids: businessIdsByDeal[deal.id] || [] })).filter(deal => !activeBusinessUnitId || visibleDealIds.has(deal.id)))
    setClients((clientsData || []).filter(client => !activeBusinessUnitId || visibleClientIds.has(client.id)))
    setProjects((projectsData || []).filter(project => !activeBusinessUnitId || project.business_unit_id === activeBusinessUnitId))
    setProfiles(profilesData || [])
    setAllocationsMap(aMap)
    setRightsMap(rMap)
    setDeliveriesMap(dMap)
    setLoading(false)
  }

  async function fetchExchangeRates() {
    const { data: stored } = await supabase.from('exchange_rate_snapshots').select('*').order('rate_date', { ascending: false }).limit(1).maybeSingle()
    if (stored) setRateSnapshot(stored)

    const fetchedAt = stored?.fetched_at ? new Date(stored.fetched_at).getTime() : 0
    if (Date.now() - fetchedAt < 12 * 60 * 60 * 1000) return

    const { data, error } = await supabase.functions.invoke('refresh-exchange-rates')
    if (!error && data?.rates) setRateSnapshot(data)
  }

  function resetForm() {
    setForm({ ...EMPTY_FORM, business_unit_ids: activeBusinessUnitId ? [activeBusinessUnitId] : [] })
    setAllocations([{ ...EMPTY_ALLOC }])
    setEditingDealId(null)
    setShowForm(false)
  }

  function openNewDealForm() {
    if (showForm && !editingDealId) {
      resetForm()
      return
    }

    setForm({ ...EMPTY_FORM, business_unit_ids: activeBusinessUnitId ? [activeBusinessUnitId] : [] })
    setAllocations([{ ...EMPTY_ALLOC }])
    setEditingDealId(null)
    setShowForm(true)
    setOpenActionMenu(null)
  }

  function openEditDeal(deal) {
    const existingAllocations = allocationsMap[deal.id] || []
    const existingRights = rightsMap[deal.id] || []

    setForm({
      client_id: deal.client_id || '',
      name: deal.name || '',
      deal_type: deal.deal_type || '',
      value: deal.value ? String(deal.value) : '',
      currency: deal.currency || 'GBP',
      start_date: deal.start_date || '',
      end_date: deal.end_date || '',
      status: deal.status || 'active',
      notes: deal.notes || '',
      assigned_to: deal.assigned_to || '',
      payment_terms: deal.payment_terms || '',
      business_unit_ids: deal.business_unit_ids || []
    })

    setAllocations(
      existingAllocations.length > 0
        ? existingAllocations.map(a => ({
            project_id: a.project_id || '',
            amount: a.amount ? String(a.amount) : '',
            notes: a.notes || '',
            rights: existingRights
              .filter(r => r.project_id === a.project_id)
              .map(r => ({
                name: r.name || '',
                right_type: r.right_type || '',
                quantity: r.quantity ? String(r.quantity) : '',
                notes: r.notes || ''
              }))
          }))
        : [{ ...EMPTY_ALLOC }]
    )

    setEditingDealId(deal.id)
    setShowForm(true)
    setOpenActionMenu(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function openRightModal(allocationIndex) {
    setRightModalAllocationIndex(allocationIndex)
    setRightModalForm(EMPTY_RIGHT_MODAL_FORM)
    setShowRightModal(true)
  }

  function addRightFromModal() {
    const rightName = getRightName(rightModalForm)

    if (!rightName) {
      toast.error('Right name is required')
      return
    }

    setAllocations(allocations.map((allocation, index) => {
      if (index !== rightModalAllocationIndex) return allocation

      return {
        ...allocation,
        rights: [
          ...(allocation.rights || []),
          {
            name: rightName,
            right_type: rightModalForm.category === 'Custom' ? 'Custom' : rightModalForm.category,
            quantity: rightModalForm.quantity || '1',
            notes: rightModalForm.notes || ''
          }
        ]
      }
    }))

    setShowRightModal(false)
    setRightModalAllocationIndex(null)
    setRightModalForm(EMPTY_RIGHT_MODAL_FORM)
  }

  async function saveDeal() {
    if (!form.name.trim()) {
      toast.error('Deal name is required')
      return
    }

    if (!form.client_id) {
      toast.error('Please select a client')
      return
    }
    if (!(form.business_unit_ids || []).length) {
      toast.error('Select at least one Business')
      return
    }

    setSaving(true)

    const dealPayload = {
      ...form,
      client_id: form.client_id || null,
      assigned_to: form.assigned_to || null,
      value: form.value ? parseMoneyInput(form.value) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    }
    delete dealPayload.business_unit_ids

    let dealId = editingDealId

    if (editingDealId) {
      const { error: updateError } = await supabase
        .from('deals')
        .update(withOrg(dealPayload, orgId))
        .eq('id', editingDealId)

      if (updateError) {
        toast.error(`Failed to update deal: ${updateError.message}`)
        setSaving(false)
        return
      }

      const { error: deleteRightsError } = await supabase
        .from('rights')
        .delete()
        .eq('deal_id', editingDealId)
        .eq('org_id', orgId)
      if (deleteRightsError) {
        toast.error(`Deal updated, but rights failed: ${deleteRightsError.message}`)
        setSaving(false)
        fetchAll()
        return
      }

      const { error: deleteAllocError } = await supabase
        .from('deal_project_allocations')
        .delete()
        .eq('deal_id', editingDealId)
        .eq('org_id', orgId)
      if (deleteAllocError) {
        toast.error(`Deal updated, but allocations failed: ${deleteAllocError.message}`)
        setSaving(false)
        fetchAll()
        return
      }
    } else {
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .insert([withOrg(dealPayload, orgId)])
        .select('id')
        .single()

      if (dealError) {
        toast.error(`Failed to add deal: ${dealError.message}`)
        setSaving(false)
        return
      }

      dealId = dealData.id
    }

    const validAllocs = allocations
      .map(a => ({ ...a, amountValue: parseMoneyInput(a.amount) }))
      .filter(a => a.project_id && a.amountValue > 0)

    if (validAllocs.length > 0) {
      const allocationPayload = validAllocs.map(a => withOrg({
        deal_id: dealId,
        project_id: a.project_id || null,
        amount: a.amountValue,
        currency: form.currency,
        notes: a.notes || null
      }, orgId))

      const { data: createdAllocations, error: allocationError } = await supabase
        .from('deal_project_allocations')
        .insert(allocationPayload)
        .select('id')

      if (allocationError) {
        toast.error(`Deal saved, but allocations failed: ${allocationError.message}`)
        setSaving(false)
        fetchAll()
        return
      }

      if ((createdAllocations || []).length !== allocationPayload.length) {
        toast.error('Deal saved, but not all project allocations were created. Please try again.')
        setSaving(false)
        fetchAll()
        return
      }
    }

    const rightsPayload = []

    validAllocs.forEach(a => {
      ;(a.rights || []).forEach(r => {
        if (!r.name.trim()) return

        rightsPayload.push(withOrg({
          deal_id: dealId,
          project_id: a.project_id || null,
          name: r.name.trim(),
          right_type: r.right_type || null,
          quantity: r.quantity ? Number(r.quantity) : 1,
          notes: r.notes || null
        }, orgId))
      })
    })

    if (rightsPayload.length > 0) {
      const { data: createdRights, error: rightsError } = await supabase
        .from('rights')
        .insert(rightsPayload)
        .select()

      if (rightsError) {
        toast.error(`Deal saved, but rights failed: ${rightsError.message}`)
        setSaving(false)
        fetchAll()
        return
      }

      const deliveryPayload = []

      ;(createdRights || []).forEach(right => {
        const quantity = Number(right.quantity) || 1

        for (let i = 0; i < quantity; i += 1) {
          deliveryPayload.push(withOrg({
            right_id: right.id,
            name: `${right.name} ${i + 1}`,
            status: 'pending',
            channel: null,
            delivery_date: null,
            url: null,
            notes: null
          }, orgId))
        }
      })

      if (deliveryPayload.length > 0) {
        const { error: deliveryError } = await supabase
          .from('deliveries')
          .insert(deliveryPayload)

        if (deliveryError) {
          toast.error(`Rights saved, but delivery items failed: ${deliveryError.message}`)
          setSaving(false)
          fetchAll()
          return
        }
      }
    }

    const { error: businessError } = await supabase.rpc('replace_deal_business_units', {
      target_deal_id: dealId,
      target_business_unit_ids: form.business_unit_ids
    })
    if (businessError) {
      toast.error(`Deal saved, but Business assignment failed: ${businessError.message}`)
      setSaving(false)
      fetchAll()
      return
    }

    setSaving(false)
    toast.success(editingDealId ? 'Deal updated successfully' : 'Deal added successfully')
    resetForm()
    fetchAll()
  }

  async function deleteDeal(id) {
    const { error } = await supabase.from('deals').delete().eq('id', id)

    if (error) {
      toast.error(`Failed to delete deal: ${error.message}`)
    } else {
      toast.success('Deal deleted')
    }

    setConfirmDelete(null)
    setOpenActionMenu(null)
    fetchAll()
  }

  function addAllocRow() {
    setAllocations([...allocations, { ...EMPTY_ALLOC }])
  }

  function removeAllocRow(i) {
    if (allocations.length === 1) {
      setAllocations([{ ...EMPTY_ALLOC }])
      return
    }

    setAllocations(allocations.filter((_, idx) => idx !== i))
  }

  function updateAlloc(i, field, value) {
    setAllocations(allocations.map((a, idx) => idx === i ? { ...a, [field]: value } : a))
    if (field === 'project_id') {
      const projectBusinessId = projects.find(project => project.id === value)?.business_unit_id
      if (projectBusinessId && !(form.business_unit_ids || []).includes(projectBusinessId)) {
        setForm(current => ({ ...current, business_unit_ids: [...(current.business_unit_ids || []), projectBusinessId] }))
      }
    }
  }

  function removeRightRow(allocationIndex, rightIndex) {
    setAllocations(allocations.map((a, idx) => {
      if (idx !== allocationIndex) return a
      return { ...a, rights: (a.rights || []).filter((_, rIdx) => rIdx !== rightIndex) }
    }))
  }

  function updateRight(allocationIndex, rightIndex, field, value) {
    setAllocations(allocations.map((a, idx) => {
      if (idx !== allocationIndex) return a

      return {
        ...a,
        rights: (a.rights || []).map((r, rIdx) =>
          rIdx === rightIndex ? { ...r, [field]: value } : r
        )
      }
    }))
  }

  const totalAllocated = allocations.reduce((sum, a) => sum + parseMoneyInput(a.amount), 0)
  const dealValue = parseMoneyInput(form.value)
  const unallocated = dealValue - totalAllocated
  const sym = currencySymbol(form.currency)

  const enrichedDeals = deals.map(deal => {
    const dealAllocs = allocationsMap[deal.id] || []
    const dealRights = rightsMap[deal.id] || []
    const dealDeliveries = deliveriesMap[deal.id] || []
    const allocated = dealAllocs.reduce((sum, a) => sum + (Number(a.amount) || 0), 0)
    const healthScore = getDeliveryProgress(dealDeliveries, deal)

    return {
      ...deal,
      allocated,
      projectCount: dealAllocs.length,
      rightsCount: dealRights.length,
      deliveryCount: dealDeliveries.length,
      completedDeliveryCount: getCompletedDeliveryCount(dealDeliveries),
      healthScore,
      progressLabel: getProgressLabel(healthScore),
      statusBadge: getTimelineStatus(deal, dealDeliveries),
    }
  })

  const filtered = enrichedDeals
    .filter(deal => {
      const matchesSearch =
        (deal.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (deal.clients?.name || '').toLowerCase().includes(search.toLowerCase())

      const matchesStatus = filterStatus ? deal.status === filterStatus : true
      const matchesClient = filterClient ? deal.client_id === filterClient : true

      return matchesSearch && matchesStatus && matchesClient
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'deal_asc': return (a.name || '').localeCompare(b.name || '')
        case 'deal_desc': return (b.name || '').localeCompare(a.name || '')
        case 'client_asc': return (a.clients?.name || '').localeCompare(b.clients?.name || '')
        case 'client_desc': return (b.clients?.name || '').localeCompare(a.clients?.name || '')
        case 'value_asc': return Number(a.value || 0) - Number(b.value || 0)
        case 'end_date_asc': return new Date(a.end_date || '9999-12-31') - new Date(b.end_date || '9999-12-31')
        case 'created_asc': return new Date(a.created_at || 0) - new Date(b.created_at || 0)
        case 'created_desc': return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        case 'value_desc':
        default: return Number(b.value || 0) - Number(a.value || 0)
      }
    })

  const totalValueByCurrency = enrichedDeals.reduce((totals, deal) => {
    const currency = deal.currency || 'GBP'
    totals[currency] = (totals[currency] || 0) + (Number(deal.value) || 0)
    return totals
  }, {})
  const totalAllocatedValueByCurrency = enrichedDeals.reduce((totals, deal) => {
    const dealAllocs = allocationsMap[deal.id] || []

    dealAllocs.forEach(allocation => {
      const currency = allocation.currency || deal.currency || 'GBP'
      totals[currency] = (totals[currency] || 0) + (Number(allocation.amount) || 0)
    })

    return totals
  }, {})
  const convertedTotalValue = rateSnapshot
    ? sumInCurrency(enrichedDeals, reportingCurrency, rateSnapshot.rates)
    : null
  const convertedAllocatedValue = rateSnapshot
    ? sumInCurrency(
        enrichedDeals.flatMap(deal => (allocationsMap[deal.id] || []).map(allocation => ({ ...allocation, dealCurrency: deal.currency || 'GBP' }))),
        reportingCurrency,
        rateSnapshot.rates,
        allocation => allocation.amount,
        allocation => allocation.currency || allocation.dealCurrency
      )
    : null
  const activeDeals = enrichedDeals.filter(d => d.status === 'active').length
  const avgHealth = enrichedDeals.length
    ? Math.round(enrichedDeals.reduce((sum, d) => sum + d.healthScore, 0) / enrichedDeals.length)
    : 0

  const hasFilters = search || filterStatus || filterClient
  const effectiveViewMode = viewMode || (enrichedDeals.length > 6 ? 'list' : 'cards')
  const dealToDelete = confirmDelete ? deals.find(d => d.id === confirmDelete) : null

  function changeViewMode(mode) {
    setViewMode(mode)
    localStorage.setItem('delivr-deals-view', mode)
    setOpenActionMenu(null)
  }

  function changeReportingCurrency(currency) {
    setReportingCurrency(currency)
    localStorage.setItem(`delivr.reporting-currency.${orgId}`, currency)
  }

  return (
    <div className="hv-page">
      <style>{`
        .deals-topbar { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 26px; }
        .deals-actions { display: flex; align-items: center; gap: 10px; }
        .deals-search { position: relative; width: 300px; }

        .deals-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .deals-reporting-bar { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin: -8px 0 10px; color: var(--hv-text-muted); font-size: 11px; }
        .deals-reporting-bar label { font-weight: 700; }
        .deals-reporting-bar .hv-select { width: 92px; height: 32px; min-height: 32px; padding-top: 4px; padding-bottom: 4px; font-size: 11px; }
        .deals-rate-note { margin: -10px 0 18px; color: var(--hv-text-muted); font-size: 10px; text-align: right; }

        .deals-summary-item { display: flex; min-width: 0; align-items: center; gap: 14px; padding: 17px 18px; border: 1px solid var(--hv-border); border-radius: 8px; background: #fff; box-shadow: var(--hv-shadow-sm); }
        .deals-summary-icon { display: inline-flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 50%; }
        .deals-summary-icon.indigo { color: #4F46E5; background: #EEF2FF; }
        .deals-summary-icon.green { color: #059669; background: #E8F8F2; }
        .deals-summary-icon.blue { color: #2563EB; background: #EFF6FF; }
        .deals-summary-icon.amber { color: #D97706; background: #FFF7E6; }
        .deals-summary-copy { min-width: 0; flex: 1; }
        .deals-summary-arrow { flex: 0 0 auto; color: #91A0BC; }
        .deals-summary-value { font-size: 22px; font-weight: 800; color: var(--hv-text-primary); letter-spacing: -0.6px; line-height: 1; margin-bottom: 6px; }
        .deals-summary-label { font-size: 11px; font-weight: 700; color: var(--hv-text-muted); }

        .deals-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }

        .deals-view-toggle { display: inline-flex; flex: 0 0 auto; padding: 3px; border: 1px solid var(--hv-border); border-radius: 8px; background: #fff; }
        .deals-view-toggle button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 28px; padding: 0; border: 0; border-radius: 5px; background: transparent; color: var(--hv-text-muted); cursor: pointer; }
        .deals-view-toggle button.active { background: var(--hv-accent-light); color: var(--hv-accent); }

        .deals-list-wrap { overflow: visible; }
        .deals-list { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .deals-list th { position: sticky; top: 0; z-index: 2; padding: 11px 14px; border-bottom: 1px solid var(--hv-border); background: #F8FAFD; color: var(--hv-text-muted); font-size: 10px; font-weight: 800; text-align: left; text-transform: uppercase; }
        .deals-list td { padding: 13px 14px; border-bottom: 1px solid var(--hv-border-light); color: var(--hv-text-secondary); font-size: 12px; vertical-align: middle; }
        .deals-list tbody tr { background: #fff; cursor: pointer; transition: background 0.12s ease; }
        .deals-list tbody tr:hover { background: var(--hv-surface-hover); }
        .deals-list tbody tr:last-child td { border-bottom: 0; }
        .deals-list-name { display: block; overflow: hidden; color: var(--hv-text-primary); font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
        .deals-list-client { display: block; margin-top: 3px; overflow: hidden; color: var(--hv-text-muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
        .deals-list-value { color: var(--hv-text-primary); font-weight: 800; }
        .deals-list-progress { display: grid; grid-template-columns: minmax(70px, 1fr) 34px; align-items: center; gap: 8px; }
        .deals-list-progress-track { height: 6px; overflow: hidden; border-radius: 3px; background: #EEF2F7; }
        .deals-list-progress-track span { display: block; height: 100%; border-radius: inherit; background: #6366F1; }
        .deals-list-progress strong { color: #6366F1; font-size: 10px; text-align: right; }
        .deals-list-health { display: inline-flex; align-items: center; padding: 4px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; white-space: nowrap; }
        .deal-list-actions { position: relative; text-align: right; }
        .deal-list-menu-trigger { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: var(--hv-text-muted); cursor: pointer; }
        .deal-list-menu-trigger:hover, .deal-list-menu-trigger:focus-visible { background: var(--hv-accent-light); color: var(--hv-accent); outline: none; }
        .deal-list-actions .deal-menu { top: 36px; right: 8px; text-align: left; }

        .deal-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15,21,35,0.05);
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;
          position: relative;
        }

        .deal-card:hover { transform: translateY(-2px); box-shadow: 0 14px 35px rgba(15,21,35,0.08); border-color: #D8DEEB; }
        .deal-card-body { padding: 20px; }
        .deal-card-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
        .deal-name { font-size: 14px; font-weight: 800; color: var(--hv-text-primary); line-height: 1.25; }
        .deal-client { font-size: 11.5px; color: var(--hv-text-muted); margin-top: 4px; }
        .deal-card-mid { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; margin-top: 28px; margin-bottom: 18px; }
        .deal-value { font-size: 22px; font-weight: 800; color: var(--hv-text-primary); letter-spacing: -0.65px; margin-bottom: 4px; }
        .deal-mini-label { font-size: 11px; color: var(--hv-text-muted); }
        .deal-stat { margin-top: 18px; font-size: 18px; font-weight: 800; color: var(--hv-text-primary); }

        .health-ring-small { width: 62px; height: 62px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .health-ring-inner-small { width: 46px; height: 46px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; }

        .deal-card-footer {
          border-top: 1px solid var(--hv-border-light);
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: var(--hv-text-muted);
          font-size: 11px;
        }

        .deal-menu {
          position: absolute;
          top: 42px;
          right: 16px;
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 12px;
          box-shadow: 0 14px 40px rgba(15,21,35,0.16);
          padding: 6px;
          z-index: 20;
          min-width: 140px;
        }

        .right-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 24px;
        }

        .right-modal {
          width: 560px;
          max-width: 100%;
          background: #fff;
          border-radius: 16px;
          border: 1px solid var(--hv-border);
          box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
          padding: 24px;
        }

        @media (max-width: 1100px) {
          .deals-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .deals-summary { grid-template-columns: repeat(2, 1fr); row-gap: 18px; }
          .deals-summary-item { padding: 17px 18px; }
        }
      `}</style>

      <div className="deals-topbar">
        <div>
          <h1 className="hv-page-title">Deals</h1>
          <p className="hv-page-subtitle">Manage commercial agreements, budgets and delivery progress.</p>
        </div>

        <div className="deals-actions">
          <div className="deals-search">
            <span className="hv-search-icon"><Search size={14} /></span>
            <input className="hv-search" type="text" placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <select className="hv-select" style={{ width: 150, height: 36 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {['draft', 'active', 'completed', 'cancelled'].map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>

          <select className="hv-select" style={{ width: 150, height: 36 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select className="hv-select" style={{ width: 170, height: 36 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="value_desc">Value High → Low</option>
            <option value="value_asc">Value Low → High</option>
            <option value="created_desc">Newest First</option>
            <option value="created_asc">Oldest First</option>
            <option value="deal_asc">Deal A → Z</option>
            <option value="deal_desc">Deal Z → A</option>
            <option value="client_asc">Client A → Z</option>
            <option value="client_desc">Client Z → A</option>
            <option value="end_date_asc">Next Deadline</option>
          </select>

          <div className="deals-view-toggle" aria-label="Deal view">
            <button className={effectiveViewMode === 'list' ? 'active' : ''} onClick={() => changeViewMode('list')} title="List view" aria-label="List view"><List size={16} /></button>
            <button className={effectiveViewMode === 'cards' ? 'active' : ''} onClick={() => changeViewMode('cards')} title="Card view" aria-label="Card view"><LayoutGrid size={16} /></button>
          </div>

          {canManageDeals && <button className="hv-btn hv-btn-primary" onClick={openNewDealForm}>
            {showForm && !editingDealId ? 'Cancel' : (
              <>
                <Plus size={15} />
                New Deal
              </>
            )}
          </button>}
        </div>
      </div>

      {showForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
                {editingDealId ? 'Edit Deal' : 'New Deal'}
              </div>
              <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)' }}>
                Enter the deal details, allocate the budget across projects and optionally add rights.
              </p>
            </div>

            <button className="hv-btn hv-btn-ghost" onClick={resetForm}>
              Cancel
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label className="hv-form-label">Client <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <select className="hv-select" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Select a client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Deal Name <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input className="hv-input" placeholder="e.g. Annual Sponsorship Agreement 2026" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>

            {businessUnits.length > 1 && <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Businesses <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {businessUnits.map(unit => {
                  const selected = (form.business_unit_ids || []).includes(unit.id)
                  return <label key={unit.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 11px', border: '1px solid var(--hv-border)', borderRadius: 7, cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected} onChange={() => setForm({
                      ...form,
                      business_unit_ids: selected
                        ? form.business_unit_ids.filter(id => id !== unit.id)
                        : [...(form.business_unit_ids || []), unit.id]
                    })} />
                    <span>{unit.name}</span>
                  </label>
                })}
              </div>
              <div style={{ marginTop: 7, color: 'var(--hv-text-muted)', fontSize: 11.5 }}>
                Businesses used by project allocations must remain selected.
              </div>
            </div>}

            {canManageFinancials && <div>
              <label className="hv-form-label">Total Deal Value</label>
              <input className="hv-input" type="text" inputMode="numeric" placeholder="0" value={formatWithCommas(form.value)} onChange={e => setForm({ ...form, value: stripCommas(e.target.value) })} />
            </div>}

            <div>
              <label className="hv-form-label">Currency</label>
              <select className="hv-select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {['GBP', 'USD', 'EUR', 'AUD', 'CAD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Deal Type</label>
              <input className="hv-input" placeholder="e.g. Title, Associate, Media" value={form.deal_type} onChange={e => setForm({ ...form, deal_type: e.target.value })} />
            </div>

            {canManageFinancials && <div>
              <label className="hv-form-label">Payment Terms</label>
            <input className="hv-input" placeholder="e.g. 50% upfront, 50% on completion" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} />
           </div>}

            <div>
              <label className="hv-form-label">Status</label>
              <select className="hv-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['draft', 'active', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Start Date</label>
              <input className="hv-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">End Date</label>
              <input className="hv-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Assign To</label>
              <select className="hv-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Unassigned</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Notes</label>
              <input className="hv-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {canManageFinancials && <div style={{ borderTop: '1px solid var(--hv-border)', paddingTop: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>Project Allocations</div>
                <div style={{ fontSize: '12.5px', color: 'var(--hv-text-muted)', marginTop: '2px' }}>
                  Split this deal's budget across projects. Rights are optional and can be added now or later.
                </div>
              </div>

              {dealValue > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: unallocated < 0 ? 'var(--hv-red)' : unallocated === 0 ? 'var(--hv-green)' : 'var(--hv-text-muted)' }}>
                    {sym}{unallocated < 0 ? `-${Math.abs(unallocated).toLocaleString()}` : unallocated.toLocaleString()} {unallocated < 0 ? 'over' : unallocated === 0 ? 'fully allocated' : 'remaining'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--hv-text-muted)' }}>
                    {sym}{totalAllocated.toLocaleString()} of {sym}{dealValue.toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            {allocations.map((alloc, i) => (
              <div key={i} style={{ border: '1px solid var(--hv-border)', borderRadius: 'var(--hv-radius)', padding: '14px', marginBottom: '12px', background: '#FAFBFD' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label className="hv-form-label">Project</label>
                    <select className="hv-select" value={alloc.project_id} onChange={e => updateAlloc(i, 'project_id', e.target.value)}>
                      <option value="">Select a project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="hv-form-label">Amount</label>
                    <input className="hv-input" type="text" inputMode="numeric" placeholder="0" value={formatWithCommas(alloc.amount)} onChange={e => updateAlloc(i, 'amount', stripCommas(e.target.value))} />
                  </div>

                  <button className="hv-btn hv-btn-danger" style={{ fontSize: '12px', padding: '9px 10px' }} onClick={() => removeAllocRow(i)}>
                    ×
                  </button>
                </div>

                <div style={{ marginTop: '10px' }}>
                  <label className="hv-form-label">Allocation Notes</label>
                  <input className="hv-input" value={alloc.notes} onChange={e => updateAlloc(i, 'notes', e.target.value)} />
                </div>

                <div style={{ marginTop: '14px', borderTop: '1px solid var(--hv-border)', paddingTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>Optional Rights</div>
                      <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>Add commercial rights for this project allocation now, or leave blank and add later.</div>
                    </div>

                    <button className="hv-btn hv-btn-secondary" style={{ fontSize: '12px' }} onClick={() => openRightModal(i)}>
                      + Add right
                    </button>
                  </div>

                  {(alloc.rights || []).length === 0 ? (
                    <div style={{ fontSize: '12.5px', color: 'var(--hv-text-muted)', padding: '8px 0' }}>
                      No rights added for this allocation.
                    </div>
                  ) : (
                    (alloc.rights || []).map((right, rIdx) => (
                      <div key={rIdx} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 90px auto', gap: '10px', marginBottom: '10px', alignItems: 'end' }}>
                        <div>
                          {rIdx === 0 && <label className="hv-form-label">Right Name</label>}
                          <input className="hv-input" value={right.name} onChange={e => updateRight(i, rIdx, 'name', e.target.value)} />
                        </div>

                        <div>
                          {rIdx === 0 && <label className="hv-form-label">Type</label>}
                          <input className="hv-input" value={right.right_type} onChange={e => updateRight(i, rIdx, 'right_type', e.target.value)} />
                        </div>

                        <div>
                          {rIdx === 0 && <label className="hv-form-label">Qty</label>}
                          <input className="hv-input" type="number" value={right.quantity} onChange={e => updateRight(i, rIdx, 'quantity', e.target.value)} />
                        </div>

                        <button className="hv-btn hv-btn-danger" style={{ fontSize: '12px', padding: '9px 10px' }} onClick={() => removeRightRow(i, rIdx)}>
                          ×
                        </button>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <input className="hv-input" placeholder="Notes for this right" value={right.notes} onChange={e => updateRight(i, rIdx, 'notes', e.target.value)} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}

            <button className="hv-btn hv-btn-secondary" style={{ fontSize: '13px' }} onClick={addAllocRow}>
              + Add project
            </button>
          </div>}

          <button className="hv-btn hv-btn-primary" onClick={saveDeal} disabled={saving}>
            {saving ? 'Saving...' : editingDealId ? 'Update Deal' : 'Save Deal'}
          </button>
        </div>
      )}

      {canViewFinancials && <div className="deals-reporting-bar"><label htmlFor="deals-reporting-currency">Reporting currency</label><select id="deals-reporting-currency" className="hv-select" value={reportingCurrency} onChange={event => changeReportingCurrency(event.target.value)}>{REPORTING_CURRENCIES.map(currency => <option key={currency} value={currency}>{currency}</option>)}</select></div>}

      <div className="deals-summary">
        <SummaryItem value={enrichedDeals.length} label="Total Deals" icon={<Handshake size={21} />} tone="indigo" />
        {canViewFinancials && <SummaryItem value={convertedTotalValue === null ? formatCurrencyTotals(totalValueByCurrency) : formatMoney(convertedTotalValue, reportingCurrency)} label="Total Value" icon={<CircleDollarSign size={21} />} tone="green" />}
        {canViewFinancials && <SummaryItem value={convertedAllocatedValue === null ? formatCurrencyTotals(totalAllocatedValueByCurrency) : formatMoney(convertedAllocatedValue, reportingCurrency)} label="Allocated" icon={<WalletCards size={21} />} tone="blue" />}
        <SummaryItem
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 34, height: 34, borderRadius: '50%', background: `conic-gradient(#6366F1 ${avgHealth * 3.6}deg, #EEF2F7 0deg)`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                  {avgHealth}
                </span>
              </span>
              <span>{activeDeals}</span>
            </span>
          }
          label="Active Deals"
          icon={<Activity size={21} />}
          tone="amber"
        />
      </div>
      {canViewFinancials && <div className="deals-rate-note">{rateSnapshot ? `Converted using ECB reference rates from ${formatDate(rateSnapshot.rate_date)}. Individual deals retain their original currency.` : 'Live conversion unavailable. Totals are shown in their original currencies.'}</div>}

      {error && (
        <div style={{ background: 'var(--hv-red-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--hv-radius)', padding: '14px 18px', color: 'var(--hv-red-text)', fontSize: '13.5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: '13.5px', padding: '40px 0' }}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">{hasFilters ? 'No deals match your filters' : 'No deals yet'}</div>
            <div className="hv-empty-text">{hasFilters ? 'Try adjusting your search.' : 'Add your first deal using the button above.'}</div>
          </div>
        </div>
      ) : effectiveViewMode === 'list' ? (
        <div className="hv-table-wrap deals-list-wrap">
          <table className="deals-list">
            <thead><tr><th style={{ width: '23%' }}>Deal</th>{canViewFinancials && <th style={{ width: '12%' }}>Value</th>}<th style={{ width: '9%' }}>Projects</th><th style={{ width: '12%' }}>Deliveries</th><th style={{ width: '17%' }}>Progress</th><th style={{ width: '13%' }}>Health</th><th style={{ width: '12%' }}>End date</th><th style={{ width: 48 }}></th></tr></thead>
            <tbody>{filtered.map(deal => <DealListRow key={deal.id} deal={deal} openActionMenu={openActionMenu} setOpenActionMenu={setOpenActionMenu} actionMenuRef={actionMenuRef} onOpen={() => navigate(`/deals/${deal.id}`)} onEdit={() => openEditDeal(deal)} onDelete={() => setConfirmDelete(deal.id)} canViewFinancials={canViewFinancials} canManageDeals={canManageDeals} />)}</tbody>
          </table>
        </div>
      ) : (
        <div className="deals-grid">
          {filtered.map(deal => (
            <DealCard
              key={deal.id}
              deal={deal}
              openActionMenu={openActionMenu}
              setOpenActionMenu={setOpenActionMenu}
              actionMenuRef={actionMenuRef}
              onOpen={() => navigate(`/deals/${deal.id}`)}
              onEdit={() => openEditDeal(deal)}
              onDelete={() => setConfirmDelete(deal.id)}
              canViewFinancials={canViewFinancials}
              canManageDeals={canManageDeals}
            />
          ))}
        </div>
      )}

      {showRightModal && (
        <div className="right-modal-overlay">
          <div className="right-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Add Right</div>
                <div style={{ fontSize: 13, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  Add a commercial right to this project allocation.
                </div>
              </div>

              <button className="hv-btn hv-btn-ghost" onClick={() => setShowRightModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="hv-form-label">Right Category</label>
                <select
                  className="hv-select"
                  value={rightModalForm.category}
                  onChange={e => setRightModalForm({ ...rightModalForm, category: e.target.value })}
                >
                  {RIGHT_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {rightModalForm.category === 'Custom' && (
                <div>
                  <label className="hv-form-label">Custom Right Name</label>
                  <input
                    className="hv-input"
                    placeholder="e.g. Founder Interview Series"
                    value={rightModalForm.custom_name}
                    onChange={e => setRightModalForm({ ...rightModalForm, custom_name: e.target.value })}
                  />
                </div>
              )}

              <div>
                <label className="hv-form-label">Quantity</label>
                <input
                  className="hv-input"
                  type="number"
                  min="1"
                  value={rightModalForm.quantity}
                  onChange={e => setRightModalForm({ ...rightModalForm, quantity: e.target.value })}
                />
              </div>

              <div>
                <label className="hv-form-label">Notes</label>
                <input
                  className="hv-input"
                  placeholder="Optional notes..."
                  value={rightModalForm.notes}
                  onChange={e => setRightModalForm({ ...rightModalForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button className="hv-btn hv-btn-secondary" onClick={() => setShowRightModal(false)}>
                Cancel
              </button>

              <button className="hv-btn hv-btn-primary" onClick={addRightFromModal}>
                Add Right
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete deal"
          message={`"${dealToDelete?.name}" and all its allocations, rights and deliveries will be permanently removed.`}
          confirmLabel="Delete Deal"
          onConfirm={() => deleteDeal(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}

function SummaryItem({ value, label, icon, tone }) {
  return (
    <div className="deals-summary-item">
      <div className={`deals-summary-icon ${tone}`}>{icon}</div>
      <div className="deals-summary-copy"><div className="deals-summary-value">{value}</div><div className="deals-summary-label">{label}</div></div>
      <ChevronRight className="deals-summary-arrow" size={16} />
    </div>
  )
}

function DealCard({ deal, openActionMenu, setOpenActionMenu, actionMenuRef, onOpen, onEdit, onDelete, canViewFinancials, canManageDeals }) {
  return (
    <div className="deal-card" onClick={onOpen}>
      <div className="deal-card-body">
        <div className="deal-card-title-row">
          <div>
            <div className="deal-name">{deal.name}</div>
            <div className="deal-client">{deal.clients?.name || 'No client'}</div>
          </div>

          <button
            className="hv-btn hv-btn-ghost"
            style={{ padding: 4, color: 'var(--hv-text-muted)' }}
            onClick={e => {
              e.stopPropagation()
              setOpenActionMenu(openActionMenu === deal.id ? null : deal.id)
            }}
          >
            <MoreHorizontal size={17} />
          </button>

          {openActionMenu === deal.id && (
            <div ref={actionMenuRef} className="deal-menu" onClick={e => e.stopPropagation()}>
              <button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onOpen}>View</button>
              {canManageDeals && <button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onEdit}>Edit</button>}
              {canManageDeals && <button className="hv-btn hv-btn-danger" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onDelete}>Delete</button>}
            </div>
          )}
        </div>

        <div className="deal-card-mid">
          <div>
            {canViewFinancials && <>
              <div className="deal-value">{deal.value ? formatMoney(deal.value, deal.currency) : '—'}</div>
              <div className="deal-mini-label">Total Value</div>
            </>}

<div className="deal-stat">{deal.projectCount}</div>
<div className="deal-mini-label">
  {deal.projectCount === 1 ? 'Project' : 'Projects'}
</div>

<div className="deal-stat" style={{ marginTop: 10 }}>
  {deal.completedDeliveryCount} / {deal.deliveryCount}
</div>
<div className="deal-mini-label">
  {deal.deliveryCount === 1 ? 'Delivery' : 'Deliveries'}
</div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div
  className="health-ring-small"
  style={{
    background: `conic-gradient(#6366F1 ${deal.healthScore * 3.6}deg, #EEF2F7 0deg)`
  }}
>
              <div className="health-ring-inner-small" style={{ color: '#6366F1' }}>
                {deal.healthScore}
              </div>
            </div>

<div
  style={{
    fontSize: '12px',
    fontWeight: 700,
    color: '#6366F1'
  }}
>
  {deal.progressLabel}
</div>

<div
  style={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    padding: '4px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    color: deal.statusBadge.color,
    background: deal.statusBadge.background,
    whiteSpace: 'nowrap'
  }}
>
  {deal.statusBadge.label}
</div>
          </div>
        </div>
      </div>

      <div className="deal-card-footer">
        <span>End date</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {formatDate(deal.end_date)}
          <CalendarDays size={13} />
        </span>
      </div>
    </div>
  )
}

function DealListRow({ deal, openActionMenu, setOpenActionMenu, actionMenuRef, onOpen, onEdit, onDelete, canViewFinancials, canManageDeals }) {
  return <tr onClick={onOpen}>
    <td><span className="deals-list-name">{deal.name}</span><span className="deals-list-client">{deal.clients?.name || 'No client'}</span></td>
    {canViewFinancials && <td><span className="deals-list-value">{deal.value ? formatMoney(deal.value, deal.currency) : '—'}</span></td>}
    <td>{deal.projectCount}</td>
    <td>{deal.completedDeliveryCount} / {deal.deliveryCount}</td>
    <td><div className="deals-list-progress"><div className="deals-list-progress-track"><span style={{ width: `${deal.healthScore}%` }} /></div><strong>{deal.healthScore}%</strong></div></td>
    <td><span className="deals-list-health" style={{ color: deal.statusBadge.color, background: deal.statusBadge.background }}>{deal.statusBadge.label}</span></td>
    <td>{formatDate(deal.end_date)}</td>
    <td className="deal-list-actions" onClick={event => event.stopPropagation()}>
      <button className="deal-list-menu-trigger" aria-label={`Actions for ${deal.name}`} title="Deal actions" onClick={() => setOpenActionMenu(openActionMenu === deal.id ? null : deal.id)}><MoreHorizontal size={17} /></button>
      {openActionMenu === deal.id && <div ref={actionMenuRef} className="deal-menu"><button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onOpen}>View</button>{canManageDeals && <button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onEdit}>Edit</button>}{canManageDeals && <button className="hv-btn hv-btn-danger" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onDelete}>Delete</button>}</div>}
    </td>
  </tr>
}
